import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection, query, where, limit, onSnapshot,
  addDoc, updateDoc, doc, Timestamp, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { fsId, fmtElapsed, fmtOffset } from '../utils/formulas';
import { compressImage } from '../utils/imageUpload';
import { useToast } from '../components/Toast';

export function useIncident(user) {
  const [incident, setIncident] = useState(null);
  const [docId, setDocId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef(null);
  const toast = useToast();

  const safeUpdate = useCallback(async (id, patch) => {
    try {
      await updateDoc(doc(db, 'incidents', id), patch);
    } catch (err) {
      console.error('[useIncident] updateDoc failed:', err);
      toast('Save failed — check your connection', 'error');
    }
  }, [toast]);

  // Listen for an active incident belonging to the current user
  useEffect(() => {
    if (!user) { setIncident(null); setDocId(null); setLoading(false); return; }
    setLoading(true);
    const q = query(
      collection(db, 'incidents'),
      where('status', '==', 'active'),
      where('createdBy', '==', user.uid),
      limit(1),
    );
    const unsub = onSnapshot(q, (snap) => {
      if (snap.empty) { setIncident(null); setDocId(null); }
      else { const d = snap.docs[0]; setIncident(d.data()); setDocId(d.id); }
      setLoading(false);
    });
    return unsub;
  }, [user]);

  // Master clock — ticks every 500ms while incident is active
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!incident || incident.status !== 'active' || !incident.dispatchTime) {
      setElapsed(0);
      return;
    }
    const dispatchMs = incident.dispatchTime.toMillis?.();
    if (!dispatchMs) { setElapsed(0); return; }
    const tick = () => setElapsed(Date.now() - dispatchMs);
    tick();
    intervalRef.current = setInterval(tick, 500);
    return () => clearInterval(intervalRef.current);
  }, [incident]);

  // PAR calculations
  const parIntervalSec = (incident?.parIntervalMin || 15) * 60;
  const lastParTime = incident?.log
    ?.slice()
    .reverse()
    .find((e) => e.label === 'PAR Check' || e.label === 'PAR Acknowledged' || e.type === 'system')?.time;
  const lastParMs = lastParTime?.toMillis?.();
  const secsSinceLastPar = lastParMs ? (Date.now() - lastParMs) / 1000 : elapsed / 1000;
  const parCountdown = incident?.status === 'active'
    ? Math.max(0, parIntervalSec - (secsSinceLastPar % parIntervalSec))
    : parIntervalSec;
  const parAlert = incident?.status === 'active' && parCountdown === 0;

  // Helper to append a log entry
  const appendLog = useCallback(async (entry) => {
    if (!docId || !incident) return;
    const newEntry = { id: fsId(), time: Timestamp.now(), ...entry };
    await safeUpdate(docId, {
      log: [...(incident.log || []), newEntry],
    });
  }, [docId, incident, safeUpdate]);

  // --- Actions ---

  const createIncident = useCallback(async ({ address, incidentType, notes, checklist, isDrill }) => {
    if (!user) return;
    const now = Timestamp.now();
    const checklistItems = (checklist || []).map((item) => ({
      id: fsId(), label: item.label, checked: false, checkedAt: null,
    }));
    try {
      await addDoc(collection(db, 'incidents'), {
      address: address || '',
      incidentType: incidentType || 'other',
      notes: notes || '',
      isDrill: isDrill || false,
      status: 'active',
      dispatchTime: now,
      closedTime: null,
      parIntervalMin: 15,
      checklist: checklistItems,
      assignments: [],
      location: null,
      // Command board fields
      strategy: 'none',
      revisedStrategy: '',
      incidentInfo: {
        officer: '', initialReport: '', situationFound: '',
        incidentNumber: '', occupancyType: '', constructionType: '',
        truss: null, sprinklered: null, activated: null, specialHazards: '',
      },
      lifeSafety: {
        victimsReported: null, victimsLocation: '',
        primarySearch: 'none', primaryResults: '',
        secondarySearch: 'none', secondaryResults: '',
        utilitiesSecured: false, outsideAgencies: '',
      },
      divisions: { a: { officer: '' }, b: { officer: '' }, c: { officer: '' }, d: { officer: '' } },
      unitPositions: {},
      underControlAt: null,
      log: [{ id: fsId(), time: now, type: 'system', label: 'Incident Created', detail: '' }],
      createdBy: user.uid,
      createdByEmail: user.email || '',
      createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('[useIncident] createIncident failed:', err);
      toast('Could not create incident — try again', 'error');
    }
  }, [user, toast]);

  const closeIncident = useCallback(async () => {
    if (!docId) return;
    const now = Timestamp.now();
    await safeUpdate(docId, {
      status: 'closed',
      closedTime: now,
      log: [...(incident?.log || []), { id: fsId(), time: now, type: 'system', label: 'Incident Closed', detail: '' }],
    });
  }, [docId, incident, safeUpdate]);

  const addBenchmark = useCallback(async (benchmark) => {
    await appendLog({ type: 'benchmark', label: benchmark.label, detail: '' });
  }, [appendLog]);

  const addNote = useCallback(async (text) => {
    if (!text?.trim()) return;
    await appendLog({ type: 'note', label: 'Note', detail: text.trim() });
  }, [appendLog]);

  const acknowledgePar = useCallback(async () => {
    await appendLog({ type: 'benchmark', label: 'PAR Acknowledged', detail: '' });
  }, [appendLog]);

  const setParInterval = useCallback(async (minutes) => {
    if (!docId) return;
    await safeUpdate(docId, { parIntervalMin: minutes });
  }, [docId, safeUpdate]);

  const toggleChecklistItem = useCallback(async (itemId) => {
    if (!docId || !incident) return;
    const now = Timestamp.now();
    const updated = (incident.checklist || []).map((item) =>
      item.id === itemId
        ? { ...item, checked: !item.checked, checkedAt: !item.checked ? now : null }
        : item
    );
    const toggled = updated.find((i) => i.id === itemId);
    const logEntry = {
      id: fsId(), time: now, type: 'checklist',
      label: toggled?.checked ? 'Checklist item completed' : 'Checklist item unchecked',
      detail: toggled?.label || '',
    };
    await safeUpdate(docId, {
      checklist: updated,
      log: [...(incident.log || []), logEntry],
    });
  }, [docId, incident, safeUpdate]);

  const addChecklistItem = useCallback(async (label) => {
    if (!docId || !incident || !label?.trim()) return;
    const newItem = { id: fsId(), label: label.trim(), checked: false, checkedAt: null };
    await safeUpdate(docId, {
      checklist: [...(incident.checklist || []), newItem],
    });
  }, [docId, incident, safeUpdate]);

  const assignUnit = useCallback(async (unitId, unitName, role) => {
    if (!docId || !incident) return;
    const now = Timestamp.now();
    const existing = (incident.assignments || []).filter((a) => a.unitId !== unitId);
    const entry = { unitId, unitName: unitName || unitId, role, assignedAt: now };
    // Auto-place on map — use incident pin or fall back to Mason County center
    const positions = { ...(incident.unitPositions || {}) };
    if (!positions[unitId]) {
      const base = incident.location || { lat: 47.35, lng: -123.0 };
      const count = Object.keys(positions).length;
      const angle = (count * 45) * (Math.PI / 180);
      const offset = 0.0003;
      positions[unitId] = {
        lat: base.lat + Math.cos(angle) * offset,
        lng: base.lng + Math.sin(angle) * offset,
      };
    }
    await safeUpdate(docId, {
      assignments: [...existing, entry],
      unitPositions: positions,
      log: [...(incident.log || []), { id: fsId(), time: now, type: 'assignment', label: `${unitName || unitId} assigned`, detail: role }],
    });
  }, [docId, incident, safeUpdate]);

  const unassignUnit = useCallback(async (unitId) => {
    if (!docId || !incident) return;
    const now = Timestamp.now();
    const removed = (incident.assignments || []).find((a) => a.unitId === unitId);
    const updated = (incident.assignments || []).filter((a) => a.unitId !== unitId);
    // Remove from map positions too
    const positions = { ...(incident.unitPositions || {}) };
    delete positions[unitId];
    await safeUpdate(docId, {
      assignments: updated,
      unitPositions: positions,
      log: [...(incident.log || []), { id: fsId(), time: now, type: 'assignment', label: `${removed?.unitName || unitId} released`, detail: removed?.role || '' }],
    });
  }, [docId, incident, safeUpdate]);

  const reassignUnit = useCallback(async (unitId, newRole) => {
    if (!docId || !incident) return;
    const now = Timestamp.now();
    const updated = (incident.assignments || []).map((a) =>
      a.unitId === unitId ? { ...a, role: newRole, assignedAt: now } : a
    );
    const unit = updated.find((a) => a.unitId === unitId);
    await safeUpdate(docId, {
      assignments: updated,
      log: [...(incident.log || []), { id: fsId(), time: now, type: 'assignment', label: `${unit?.unitName || unitId} reassigned`, detail: newRole }],
    });
  }, [docId, incident, safeUpdate]);

  const splitUnit = useCallback(async (unitId, splits) => {
    if (!docId || !incident || !splits?.length) return;
    const now = Timestamp.now();
    const orig = (incident.assignments || []).find((a) => a.unitId === unitId);
    if (!orig) return;
    const baseName = orig.unitName || unitId;
    const without = (incident.assignments || []).filter((a) => a.unitId !== unitId);
    const newEntries = splits.map((s, i) => ({
      unitId: `${unitId}/${String.fromCharCode(65 + i)}`,
      unitName: `${baseName}/${String.fromCharCode(65 + i)}`,
      baseUnitId: unitId,
      role: s.role,
      personnel: s.personnel || null,
      assignedAt: now,
    }));
    const logEntries = newEntries.map((e) => ({
      id: fsId(), time: now, type: 'assignment',
      label: `${e.unitName} split`,
      detail: `${e.role}${e.personnel ? ` (${e.personnel} personnel)` : ''}`,
    }));
    await safeUpdate(docId, {
      assignments: [...without, ...newEntries],
      log: [...(incident.log || []), ...logEntries],
    });
  }, [docId, incident, safeUpdate]);

  const mergeUnit = useCallback(async (baseUnitId, role) => {
    if (!docId || !incident) return;
    const now = Timestamp.now();
    const splits = (incident.assignments || []).filter((a) => a.baseUnitId === baseUnitId);
    if (splits.length === 0) return;
    const baseName = splits[0].unitName?.split('/')[0] || baseUnitId;
    const without = (incident.assignments || []).filter((a) => a.baseUnitId !== baseUnitId);
    const merged = {
      unitId: baseUnitId,
      unitName: baseName,
      role: role || splits[0].role,
      assignedAt: now,
    };
    await safeUpdate(docId, {
      assignments: [...without, merged],
      log: [...(incident.log || []), { id: fsId(), time: now, type: 'assignment', label: `${baseName} merged`, detail: role || splits[0].role }],
    });
  }, [docId, incident, safeUpdate]);

  const updateLocation = useCallback(async (lat, lng) => {
    if (!docId) return;
    await safeUpdate(docId, { location: { lat, lng } });
  }, [docId, safeUpdate]);

  // Command board field updaters
  const updateField = useCallback(async (field, value) => {
    if (!docId) return;
    await safeUpdate(docId, { [field]: value });
  }, [docId, safeUpdate]);

  const updateNested = useCallback(async (path, value) => {
    if (!docId || !incident) return;
    // path like 'incidentInfo.officer' or 'lifeSafety.primarySearch'
    const [top, key] = path.split('.');
    const current = incident[top] || {};
    await safeUpdate(docId, {
      [top]: { ...current, [key]: value },
    });
  }, [docId, incident, safeUpdate]);

  // --- Photos ---

  const addPhoto = useCallback(async (file, caption = '') => {
    if (!docId || !user || !file) return null;
    try {
      const blob = await compressImage(file);
      const id = fsId();
      const path = `incidents/${docId}/${id}.jpg`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, blob, { contentType: 'image/jpeg' });
      const url = await getDownloadURL(ref);
      const now = Timestamp.now();
      const photo = {
        id, url, path,
        addedAt: now,
        addedBy: user.uid,
        addedByEmail: user.email || '',
        caption: caption.trim(),
        bytes: blob.size,
      };
      const existing = incident?.photos || [];
      await safeUpdate(docId, {
        photos: [...existing, photo],
        log: [...(incident?.log || []), { id: fsId(), time: now, type: 'photo', label: 'Photo added', detail: caption.trim() }],
      });
      return photo;
    } catch (err) {
      console.error('[useIncident] addPhoto failed:', err);
      toast(`Photo upload failed — ${err.message || 'try again'}`, 'error');
      return null;
    }
  }, [docId, incident, user, safeUpdate, toast]);

  const removePhoto = useCallback(async (photoId) => {
    if (!docId || !incident) return;
    const photo = (incident.photos || []).find((p) => p.id === photoId);
    if (!photo) return;
    try {
      // Try to delete the storage object — but proceed even if it fails (rules
      // may block deletion if the user wasn't the uploader, and that's fine —
      // removing the reference from the doc is what matters).
      try {
        if (photo.path) await deleteObject(storageRef(storage, photo.path));
      } catch (storageErr) {
        console.warn('[useIncident] storage delete skipped:', storageErr.message);
      }
      const remaining = (incident.photos || []).filter((p) => p.id !== photoId);
      const now = Timestamp.now();
      await safeUpdate(docId, {
        photos: remaining,
        log: [...(incident.log || []), { id: fsId(), time: now, type: 'photo', label: 'Photo removed', detail: photo.caption || '' }],
      });
    } catch (err) {
      console.error('[useIncident] removePhoto failed:', err);
      toast('Could not remove photo — try again', 'error');
    }
  }, [docId, incident, safeUpdate, toast]);

  const markUnderControl = useCallback(async () => {
    if (!docId) return;
    const now = Timestamp.now();
    await safeUpdate(docId, {
      underControlAt: now,
      log: [...(incident?.log || []), { id: fsId(), time: now, type: 'benchmark', label: 'Under Control', detail: '' }],
    });
  }, [docId, incident, safeUpdate]);

  return {
    incident,
    loading,
    elapsed,
    elapsedFmt: fmtElapsed(elapsed),
    parCountdown: Math.round(parCountdown),
    parAlert,
    createIncident,
    closeIncident,
    addBenchmark,
    addNote,
    acknowledgePar,
    setParInterval,
    toggleChecklistItem,
    addChecklistItem,
    assignUnit,
    unassignUnit,
    reassignUnit,
    splitUnit,
    mergeUnit,
    updateLocation,
    updateField,
    updateNested,
    markUnderControl,
    addPhoto,
    removePhoto,
  };
}

/* ── Report Export Utilities ── */

const fmtTs = (ts) => {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString([], { month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export function exportIncidentCSV(inc) {
  if (!inc) return '';
  const rows = [['Time', 'Offset', 'Type', 'Label', 'Detail']];
  (inc.log || []).forEach((e) => {
    rows.push([
      fmtTs(e.time),
      fmtOffset(e.time, inc.dispatchTime),
      e.type || '',
      e.label || '',
      (e.detail || '').replace(/"/g, '""'),
    ]);
  });
  return rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
}

export function exportIncidentText(inc, incidentTypes) {
  if (!inc) return '';
  const typeLabel = incidentTypes?.find((t) => t.id === inc.incidentType)?.label || inc.incidentType || 'Unknown';
  const lines = [];
  lines.push('═══════════════════════════════════════════');
  lines.push('         INCIDENT COMMAND REPORT');
  lines.push('         Central Mason Fire & EMS');
  lines.push('═══════════════════════════════════════════');
  lines.push('');
  lines.push(`Type:      ${typeLabel}`);
  lines.push(`Address:   ${inc.address || 'N/A'}`);
  lines.push(`Status:    ${inc.status || 'unknown'}`);
  lines.push(`Dispatch:  ${fmtTs(inc.dispatchTime)}`);
  if (inc.closedTime) lines.push(`Closed:    ${fmtTs(inc.closedTime)}`);
  if (inc.dispatchTime && inc.closedTime) {
    const dur = (inc.closedTime.toMillis ? inc.closedTime.toMillis() : 0) - (inc.dispatchTime.toMillis ? inc.dispatchTime.toMillis() : 0);
    lines.push(`Duration:  ${fmtElapsed(dur)}`);
  }
  if (inc.notes) lines.push(`Notes:     ${inc.notes}`);
  lines.push('');

  // Assignments
  const assigns = inc.assignments || [];
  if (assigns.length > 0) {
    lines.push('─── UNIT ASSIGNMENTS ───');
    assigns.forEach((a) => {
      const extra = a.personnel ? ` (${a.personnel} personnel)` : '';
      lines.push(`  ${(a.unitName || a.unitId).padEnd(12)} ${a.role}${extra}`);
    });
    lines.push('');
  }

  // Checklist
  const cl = inc.checklist || [];
  if (cl.length > 0) {
    const done = cl.filter((i) => i.checked).length;
    lines.push(`─── CHECKLIST (${done}/${cl.length}) ───`);
    cl.forEach((item) => {
      const mark = item.checked ? '✓' : '○';
      const ts = item.checked && item.checkedAt ? ` [${fmtOffset(item.checkedAt, inc.dispatchTime)}]` : '';
      lines.push(`  ${mark} ${item.label}${ts}`);
    });
    lines.push('');
  }

  // Activity log
  lines.push('─── ACTIVITY LOG ───');
  (inc.log || []).forEach((e) => {
    const offset = fmtOffset(e.time, inc.dispatchTime);
    const detail = e.detail ? ` — ${e.detail}` : '';
    lines.push(`  ${offset.padEnd(10)} [${(e.type || '').padEnd(10)}] ${e.label}${detail}`);
  });
  lines.push('');
  lines.push('═══════════════════════════════════════════');
  lines.push('Generated by HydroCalc · Central Mason Fire & EMS');

  return lines.join('\n');
}
